import { ref, readonly, onUnmounted, getCurrentInstance, watch, type Ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useLocaleStore } from '@/stores/locale'
import type { ClassificationInteraction } from '@/types/rules'
import api from '@/services/api'

export interface ClassifyOptions {
  product?: string
  origin?: string
  destination?: string
  useRulesMode?: boolean
  schedule?: string | null
  lang?: string
  useKeyboard?: boolean
  hs6?: boolean
  hideTheme?: boolean
  hideClose?: boolean
  useTypeform?: boolean
  useLinkOut?: boolean
  theme?: string
  forceTheme?: string
  tsc?: boolean
  echoRules?: boolean
  echo_rules?: boolean
  rules?: boolean
  avaTaxId?: string
  avaCustomerId?: string
  avataxId?: string
  avataxCustomerId?: string
  verbose?: boolean
  profile?: string
}

export interface ClassificationResult {
  hscode?: string
  hs_code?: string
  hsCode?: string
  product?: string
  productDesc?: string
  item?: string
  itemName?: string
  origin?: string
  destination?: string
  schedule?: string
  hsYear?: string
  hscodes?: Array<{ code?: string } | string>
  stacked_codes?: string[]
  duties?: Record<string, { rate?: string }>
  taxes?: unknown[]
  txId?: string
  currentSIP?: string
  sip?: string
  rules?: ClassificationInteraction[]
  orderedInteractions?: ClassificationInteraction[]
  potentialHeadings?: string[]
  currentItemInteraction?: ClassificationInteraction | null
  rulesMetaData?: {
    sip?: string
    rules?: ClassificationInteraction[]
    potentialHeadings?: string[]
    currentItemInteraction?: ClassificationInteraction | null
  }
  [key: string]: unknown
}

export interface Use3ceClassifierReturn {
  isClassifierVisible: Ref<boolean>
  classify: (options: ClassifyOptions) => Promise<ClassificationResult>
  hide: () => void
  unload: () => void
}

const isClassifierVisible = ref<boolean>(false)

// Whether a classification window is open, for UI outside it: the topbar sits
// above the window's overlay while it is, so the language can still be changed.
export const classifierOpen: Readonly<Ref<boolean>> = readonly(isClassifierVisible)

const CONTAINER_ELEMENT_ID = 'classy-vue-container'
const SECURE_IFRAME_ID = 'classyvue-secure-embed-frame'
const SECURE_EMBED_TIMEOUT_MS = 5 * 60 * 1000
// ClassyVue confirms a mounted window within seconds: `ready`, then `authenticated` once it
// holds the token. A window that never gets that far sat on its loading screen until the
// full timeout above, which read as a classifier that would not start.
const SECURE_EMBED_STARTUP_TIMEOUT_MS = 30 * 1000
const LOG_PREFIX = '[Classifier]'

/** The classification window never finished starting, so the user can simply try again. */
export class ClassifierStartError extends Error {
  constructor() {
    super('Classifier did not start')
    this.name = 'ClassifierStartError'
  }
}
const DEV_PARENT_ORIGIN = 'https://tradecompliance-ui.gamma.dev.us-west-2.aws.avalara.io'
const CLASSIFY_EMBED_ACCESS_TOKEN_HEADER = 'X-Classify-Embed-Access-Token'

let activeSecureEmbedCancel: (() => void) | null = null

interface ClassifyEnvConfig {
  CLASSIFY_EMBED_SESSION_URL?: string
  CLASSIFY_EMBED_PUBLIC_ORIGIN?: string
}

interface ClassifyEmbedSessionResponse {
  embedUrl?: string
}

const log = (...args: unknown[]): void => {
  console.log(LOG_PREFIX, ...args)
}
const warn = (...args: unknown[]): void => {
  console.warn(LOG_PREFIX, ...args)
}

const getAvalaraAccessToken = (): string | null => {
  try {
    return localStorage.getItem('access_token')
  } catch (e) {
    warn('Could not read access_token from localStorage:', e)
    return null
  }
}

const EMBED_SESSION_PATH = '/app/v1/classifier/embed-session'

const getEmbedSessionUrl = (env: ClassifyEnvConfig | null | undefined): string => {
  const endpoint = env?.CLASSIFY_EMBED_SESSION_URL || EMBED_SESSION_PATH
  if (endpoint !== EMBED_SESSION_PATH) {
    throw new Error('Classifier embed session endpoint is invalid')
  }
  return endpoint
}

const getParentOrigin = (): string => {
  const origin = window.location.origin
  const hostname = window.location.hostname
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  return isLocalhost ? DEV_PARENT_ORIGIN : origin
}

// ui-service validates locale as an ISO-639-1 alpha-2 language code, so strip region variants like en-US.
const normalizeUiLocale = (lang: string | undefined): string | undefined => {
  const languageCode = lang?.trim().split(/[-_]/)[0]?.toLowerCase()
  return languageCode && /^[a-z]{2}$/.test(languageCode) ? languageCode : undefined
}

// ui-service refuses the whole session unless each prefilled country is an ISO-3166 alpha-2 code,
// so one that is not, like a country name, is left out and picked in the window instead.
const prefillCountryCode = (field: string, value: string | undefined): string | undefined => {
  const code = value?.trim()
  if (!code) return undefined
  if (/^[A-Za-z]{2}$/.test(code)) return code.toUpperCase()
  warn(`Leaving ${field} out of the embed session: not an ISO-3166 alpha-2 code`, value)
  return undefined
}

export const validateEmbedUrl = (value: string, expectedOrigin: string | undefined): string => {
  try {
    const expected = new URL(expectedOrigin || '')
    const url = new URL(value)
    const validExpectedOrigin =
      expected.protocol === 'https:' &&
      !expected.username &&
      !expected.password &&
      expected.pathname === '/' &&
      !expected.search &&
      !expected.hash
    const validEmbedUrl =
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      url.origin === expected.origin &&
      url.pathname === '/classify/embed' &&
      Boolean(url.searchParams.get('s')?.trim())

    if (!validExpectedOrigin || !validEmbedUrl) {
      throw new Error('untrusted')
    }
    return url.toString()
  } catch {
    throw new Error('Classifier embed URL is untrusted')
  }
}

const appendRuleEngineParams = (url: string, enabled: boolean): string => {
  if (!enabled) return url

  try {
    const parsed = new URL(url, window.location.origin)
    parsed.searchParams.set('rules', 'true')
    parsed.searchParams.set('echo_rules', 'true')
    return parsed.toString()
  } catch {
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}rules=true&echo_rules=true`
  }
}

// classyvue2 hs-report.vue submits results with renamed fields
// (`item` instead of `itemName`, `product` instead of `productDesc`).
// Existing UI code reads multiple shapes (hscode/hsCode, item/itemName,
// product/productDesc), so populate the missing legacy aliases here.
const normalizeResult = (data: ClassificationResult): ClassificationResult => {
  if (!data || typeof data !== 'object') return data
  const out: ClassificationResult = { ...data }
  if (out.itemName == null && out.item != null) out.itemName = out.item
  if (out.productDesc == null && out.product != null) out.productDesc = out.product
  if (out.hsCode == null && (out.hscode != null || out.hs_code != null)) {
    out.hsCode = (out.hscode ?? out.hs_code) as string
  }
  if (out.hscode == null && out.hsCode != null) {
    out.hscode = out.hsCode
  }
  if (!out.rulesMetaData) {
    const rules = out.rules || out.orderedInteractions
    if (rules?.length || out.sip || out.currentSIP || out.potentialHeadings || out.currentItemInteraction) {
      out.rulesMetaData = {
        sip: out.sip || out.currentSIP || out.hsCode || out.hscode || out.hs_code,
        rules,
        potentialHeadings: out.potentialHeadings,
        currentItemInteraction: out.currentItemInteraction
      }
    }
  }
  return out
}

const getMessagePayload = (event: MessageEvent): Record<string, unknown> | null => {
  if (!event.data || typeof event.data !== 'object') return null
  return event.data as Record<string, unknown>
}

const getMessageType = (payload: Record<string, unknown>): string =>
  String(payload.type || payload.event || payload.name || '').toLowerCase()

const isClassificationPayload = (payload: Record<string, unknown>): boolean =>
  Boolean(payload.hsCode || payload.hscode || payload.hs_code || payload.hscodes || payload.stacked_codes)

const extractClassificationPayload = (payload: Record<string, unknown>): ClassificationResult => {
  const nested = payload.result || payload.payload || payload.data
  if (nested && typeof nested === 'object') {
    return nested as ClassificationResult
  }
  return payload as ClassificationResult
}

const buildClassifierParams = (
  options: ClassifyOptions,
  profileIds: string[]
): Record<string, unknown> => {
  const enriched: Record<string, unknown> = { ...(options as Record<string, unknown>) }
  if (!enriched.profile && profileIds.length > 0) {
    enriched.profile = profileIds[0]
  }

  // ClassyVue2's current embed resolver accepts rules/echo_rules in query
  // mode, while older integrations still use useRulesMode. Send all aliases
  // so whichever SDK/shim is deployed can pick up the intended rules trace.
  if (options.useRulesMode) {
    enriched.rules = true
    enriched.echo_rules = true
    enriched.echoRules = true
  }

  if (typeof enriched.lang === 'string') {
    enriched.lang = (enriched.lang as string).toLowerCase()
  }

  return enriched
}

const buildSecureEmbedRequest = (
  options: ClassifyOptions,
  enriched: Record<string, unknown>
): Record<string, unknown> => ({
  parent_origin: getParentOrigin(),
  prefill: {
    product_description: options.product,
    destination: prefillCountryCode('destination', options.destination),
    origin: prefillCountryCode('origin', options.origin)
  },
  ui_options: {
    locale: normalizeUiLocale(options.lang),
    import: true,
    hide_theme: options.hideTheme,
    hide_close: options.hideClose,
    use_keyboard: options.useKeyboard,
    use_link_out: options.useLinkOut,
    use_typeform: options.useTypeform,
    hs6: options.hs6,
    tsc: options.tsc,
    force_theme: options.forceTheme || options.theme,
    schedule: options.schedule,
    avatax_id: options.avataxId || options.avaTaxId,
    avatax_customer_id: options.avataxCustomerId || options.avaCustomerId,
    echo_rules: Boolean(enriched.echo_rules || enriched.echoRules || enriched.rules)
  }
})

const removeSecureIframe = (): void => {
  const existing = document.getElementById(SECURE_IFRAME_ID)
  existing?.parentNode?.removeChild(existing)
}

const classifyWithSecureEmbed = async (
  options: ClassifyOptions,
  enriched: Record<string, unknown>,
  endpoint: string,
  expectedOrigin: string | undefined,
  followLocale: boolean
): Promise<ClassificationResult> => {
  const container = document.getElementById(CONTAINER_ELEMENT_ID)
  if (!container) {
    throw new Error('Classifier container not found')
  }

  const accessToken = getAvalaraAccessToken()
  if (!accessToken) {
    throw new Error('Avalara access_token is required to create a classifier embed session')
  }

  // One embed session per classification, in the language of the options given.
  const startSession = async (sessionOptions: ClassifyOptions): Promise<string> => {
    log('Secure embed session request starting', {
      endpoint,
      product: sessionOptions.product,
      origin: sessionOptions.origin,
      destination: sessionOptions.destination,
      lang: sessionOptions.lang,
      parentOrigin: getParentOrigin(),
      hasProfile: Boolean(enriched.profile)
    })

    const { data } = await api.post<ClassifyEmbedSessionResponse>(
      endpoint,
      buildSecureEmbedRequest(sessionOptions, enriched),
      {
        headers: {
          [CLASSIFY_EMBED_ACCESS_TOKEN_HEADER]: accessToken
        }
      } as any
    )

    log('Secure embed session response received', {
      endpoint,
      hasEmbedUrl: Boolean(data?.embedUrl)
    })

    if (!data?.embedUrl) {
      throw new Error('Classifier embed session response is incomplete')
    }

    const trustedEmbedUrl = validateEmbedUrl(data.embedUrl, expectedOrigin)
    return appendRuleEngineParams(
      trustedEmbedUrl,
      Boolean(enriched.echo_rules || enriched.echoRules || enriched.rules)
    )
  }

  const mountIframe = (iframeUrl: string): HTMLIFrameElement => {
    removeSecureIframe()

    const iframe = document.createElement('iframe')
    iframe.id = SECURE_IFRAME_ID
    iframe.src = iframeUrl
    iframe.title = 'Avalara classification'
    iframe.style.width = '100%'
    iframe.style.height = '100%'
    iframe.style.border = '0'
    iframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-same-origin')
    iframe.setAttribute('referrerpolicy', 'no-referrer')
    container.appendChild(iframe)
    return iframe
  }

  const firstUrl = await startSession(options)
  activeSecureEmbedCancel?.()

  let iframe: HTMLIFrameElement | null = mountIframe(firstUrl)
  let targetOrigin = new URL(firstUrl).origin
  const localeStore = useLocaleStore()

  return new Promise((resolve, reject) => {
    let settled = false
    let authenticationSent = false
    let relaunches = 0
    let timeout = 0
    let startupTimeout = 0

    // Each classification, including one restarted in a new language, gets the full time.
    const restartTimeout = (): void => {
      clearTimeout(timeout)
      timeout = window.setTimeout(() => {
        fail(new Error('Timeout waiting for classifier result'))
      }, SECURE_EMBED_TIMEOUT_MS)
    }

    // Armed for every window mounted, cleared when that window confirms it is authenticated.
    const watchStartup = (): void => {
      clearTimeout(startupTimeout)
      startupTimeout = window.setTimeout(() => {
        fail(new ClassifierStartError())
      }, SECURE_EMBED_STARTUP_TIMEOUT_MS)
    }

    // The engine reads the product description in the session's language, so a
    // classification cannot change language midway. A new locale starts a fresh
    // classification in the same window instead; one the caller pinned stays put.
    const stopLocaleWatch = followLocale
      ? watch(
        () => localeStore.locale,
        async (next) => {
          const attempt = ++relaunches
          removeSecureIframe()
          iframe = null
          restartTimeout()
          clearTimeout(startupTimeout)
          try {
            const url = await startSession({ ...options, lang: next })
            // A newer change, or the end of the classification, supersedes this one.
            if (settled || attempt !== relaunches) return
            iframe = mountIframe(url)
            targetOrigin = new URL(url).origin
            authenticationSent = false
            watchStartup()
          } catch (error) {
            fail(error instanceof Error ? error : new Error(String(error)))
          }
        }
      )
      : (): void => undefined

    const cleanup = (): void => {
      window.removeEventListener('message', onMessage)
      stopLocaleWatch()
      clearTimeout(timeout)
      clearTimeout(startupTimeout)
      if (activeSecureEmbedCancel === cancel) {
        activeSecureEmbedCancel = null
      }
    }

    const finish = (result: ClassificationResult): void => {
      if (settled) return
      settled = true
      cleanup()
      resolve(normalizeResult(result))
    }

    const fail = (error: Error): void => {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }

    const cancel = (): void => {
      fail(new Error('Classification aborted'))
    }

    const onMessage = (event: MessageEvent): void => {
      // Only the window currently mounted counts; one replaced by a language
      // change is gone, and so is anything it still had in flight.
      if (!iframe || event.origin !== targetOrigin || event.source !== iframe.contentWindow) return
      const payload = getMessagePayload(event)
      if (!payload) return

      const type = getMessageType(payload)
      if (type === 'authenticated') {
        clearTimeout(startupTimeout)
        return
      }
      if (type === 'ready' || type === 'auth') {
        if (authenticationSent) return
        authenticationSent = true
        iframe.contentWindow?.postMessage({ type: 'auth', token: accessToken }, targetOrigin)
        return
      }

      if (
        payload.abort === true ||
        type === 'abort' ||
        type === 'close' ||
        type === 'cancel' ||
        type === 'cancelled'
      ) {
        fail(new Error('Classification aborted'))
        return
      }

      if (
        type === 'result' ||
        type === 'complete' ||
        type === 'classification_result' ||
        type === 'classification-complete' ||
        isClassificationPayload(payload)
      ) {
        finish(extractClassificationPayload(payload))
      }
    }

    restartTimeout()
    watchStartup()

    activeSecureEmbedCancel = cancel
    window.addEventListener('message', onMessage)
  })
}

export function use3ceClassifier(): Use3ceClassifierReturn {
  const authStore = useAuthStore()
  const localeStore = useLocaleStore()

  const classify = async (options: ClassifyOptions): Promise<ClassificationResult> => {
    const env = authStore.getEnvConfig()
    const profileIds = authStore.profileIds
    // Callers may pin a language; otherwise the widget opens in the language the
    // user picked for the rest of the app, and follows it if they change it.
    const followLocale = options.lang == null
    const localized = { ...options, lang: options.lang ?? localeStore.locale }
    const enriched = buildClassifierParams(localized, profileIds)
    const embedSessionUrl = getEmbedSessionUrl(env)

    log('Classify requested', {
      embedSessionUrl,
      profileCount: profileIds.length,
      product: options.product,
      origin: options.origin,
      destination: options.destination
    })

    isClassifierVisible.value = true
    try {
      const result = await classifyWithSecureEmbed(
        localized,
        enriched,
        embedSessionUrl,
        env?.CLASSIFY_EMBED_PUBLIC_ORIGIN,
        followLocale
      )
      isClassifierVisible.value = false
      removeSecureIframe()
      return result
    } catch (error) {
      isClassifierVisible.value = false
      removeSecureIframe()
      throw error
    }
  }

  const hide = (): void => {
    activeSecureEmbedCancel?.()
    isClassifierVisible.value = false
    removeSecureIframe()
  }

  const unload = (): void => {
    activeSecureEmbedCancel?.()
    removeSecureIframe()
    isClassifierVisible.value = false
  }

  if (getCurrentInstance()) {
    onUnmounted(() => {
      // Don't tear down the singleton when a single component unmounts —
      // other views may still be using the classifier. Just hide locally.
      hide()
    })
  }

  return {
    isClassifierVisible,
    classify,
    hide,
    unload
  }
}
